/* eslint-disable */
import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { defaultHomePath } from '../lib/homePath';

interface ProtectedRouteProps {
    children: ReactNode;
    /** Module codes required to access this route. If user's profile doesn't include any of these, redirect to dashboard. */
    requiredModules?: string[];
    /** If true, only admins can access (checks isAdmin() + 'admin' module). */
    adminOnly?: boolean;
    /** Staff with a linked Doctor row (or admins). */
    doctorOnly?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredModules, adminOnly, doctorOnly }) => {
    const { isAuthenticated, isLoading, user, canAccess, isAdmin } = useAuth();
    const location = useLocation();

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (user?.must_change_password && location.pathname !== '/change-password') {
        return <Navigate to="/change-password" replace />;
    }

    // Admin-only routes: check isAdmin() first, then 'admin' module
    if (adminOnly) {
        if (!isAdmin() || !canAccess('admin')) {
            return <Navigate to={defaultHomePath(user)} replace />;
        }
    }

    if (doctorOnly && !user?.doctor && !isAdmin()) {
        return <Navigate to={defaultHomePath(user)} replace />;
    }

    // Sidebar hides modules; this is the URL gate. Admins match the Médicos
    // menu (isAdmin without a doctors module) and keep staff-admin tools.
    if (requiredModules && requiredModules.length > 0 && !isAdmin()) {
        const hasAccess = requiredModules.some(mod => canAccess(mod));
        if (!hasAccess) {
            return <Navigate to={defaultHomePath(user)} replace />;
        }
    }

    return <>{children}</>;
};

export default ProtectedRoute;
